import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type Engine, type Pipeline } from '../api.ts';
import { ConfirmDanger } from '../ConfirmDanger.tsx';

/**
 * THE SETUP TAB IS THE PIPELINE LIBRARY — the owner's spec, verbatim: "I
 * want a pipeline template that I can create and when I'm submitting an
 * audio for AI first run, I get to select which pipeline I'm running", and
 * later: "no default, no champion, every pipeline is editable and
 * deletable."
 *
 * A pipeline is a NAME over an engine key, and these rows are the ONLY
 * pipelines that exist — nothing is hardcoded, nothing runs implicitly, and
 * the run dropdown lists exactly this table. Editing or deleting one touches
 * future runs only: every recording keeps the honest stamp of what actually
 * ran, under the name it ran as.
 */
/**
 * The engines a template may name. One, since the WhisperX family was retired
 * on 2026-09-22. The key matches `SCRIBE.key` on the server, which refuses
 * anything it does not recognise: a wrong constant here can only ever fail
 * loudly, at save time.
 */
const KNOWN = [
  {
    key: 'scribe',
    hint:
      'The whole recording goes to ElevenLabs Scribe once, with nothing suggested to it — no ' +
      'language, no keyterms, and the filler-removal flag deliberately left off, so hesitations ' +
      'are kept rather than tidied away. It also returns laughter and other non-speech sounds as ' +
      'their own clips, and labels every clip with which speaker said it.',
  },
] as const;

export function Setup({
  token,
  engines,
  onSignedOut,
}: {
  token: string;
  engines: Engine[];
  onSignedOut: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<Pipeline[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [condemned, setCondemned] = useState<Pipeline | null>(null);

  const [name, setName] = useState('');
  const [chain, setChain] = useState<string>(KNOWN[0].key);
  /* Derived, never stored: editing a saved pipeline sets `chain` alone, and a
     second piece of state saying whether it is known could disagree with it. */
  const known = KNOWN.find((k) => k.key === chain) ?? null;
  /** The pipeline the form is editing; null = the form creates. */
  const [editing, setEditing] = useState<Pipeline | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const { pipelines } = await api.listPipelines(token, signal);
        setRows(pipelines);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiFailure && err.status === 401) return onSignedOut();
        setError(err instanceof ApiFailure ? err.message : 'Could not load the pipelines.');
      }
    },
    [token, onSignedOut],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  function startEdit(p: Pipeline): void {
    setEditing(p);
    setName(p.name);
    setChain(p.chain);
    setNotice(null);
    setError(null);
  }

  function resetForm(): void {
    setEditing(null);
    setName('');
    setChain(KNOWN[0].key);
  }

  async function save(): Promise<void> {
    if (busy || !name.trim() || !known) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (editing) {
        await api.patchPipeline(token, editing.id, { name: name.trim(), chain });
        setNotice(`“${name.trim()}” is updated. Future runs use the new engine.`);
      } else {
        await api.createPipeline(token, { name: name.trim(), chain });
        setNotice(`“${name.trim()}” is ready — pick it on any audio row.`);
      }
      resetForm();
      await load();
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOut();
      setError(err instanceof ApiFailure ? err.message : 'Could not save it.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2>Pipelines</h2>
          <span className="topbar-spacer" />
          <button className="btn-link" type="button" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        <p className="hint">
          Named pipelines. Every AI run names one — these rows are the whole list, and each is
          editable and deletable.
        </p>

        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <div className="notice notice-ok" role="status">
            {notice}
          </div>
        )}

        <div className="adduser">
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Scribe, verbatim"
              disabled={busy}
            />
          </label>
          <div className="engine-builder">
            <div className="dial-row">
              <span className="dial-label">Engine</span>
              <div className="dial-options">
                {KNOWN.map((k) => {
                  /* The server already decided: `/admin/engines` omits any
                     engine this deployment cannot run, so a missing key is a
                     greyed button rather than a run that fails later. */
                  const runnable = engines.some((e) => e.id === k.key);
                  return (
                    <button
                      key={k.key}
                      type="button"
                      className={`dial-opt${chain === k.key ? ' is-on' : ''}`}
                      disabled={busy || !runnable}
                      title={runnable ? undefined : 'This server is missing the key it needs'}
                      onClick={() => setChain(k.key)}
                    >
                      {k.key}
                    </button>
                  );
                })}
              </div>
            </div>
            {known ? (
              <p className="hint" style={{ margin: '8px 0 0' }}>
                {known.hint}
              </p>
            ) : (
              /* A template saved before the WhisperX family was retired still
                 names one of its engines. The server refuses to run it; the
                 honest thing here is to show the stored value and ask for a
                 replacement rather than silently swap one in. */
              <p className="hint" style={{ margin: '8px 0 0' }}>
                This pipeline names <code className="engine-id">{chain}</code>, an engine this server
                no longer runs. Pick one above to replace it.
              </p>
            )}
          </div>
          <div className="btn-row" style={{ marginTop: 10 }}>
            {editing && (
              <button className="btn-quiet" type="button" disabled={busy} onClick={resetForm}>
                Cancel
              </button>
            )}
            <button
              className="btn-primary"
              type="button"
              style={{ width: 'auto' }}
              disabled={busy || !name.trim() || !known}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : editing ? `Save “${editing.name}”` : 'Create pipeline'}
            </button>
          </div>
        </div>

        {rows !== null && rows.length > 0 && (
          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Engine</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-name">{p.name}</td>
                    <td>
                      <code className="engine-id">{p.chain}</code>
                    </td>
                    <td>
                      <div className="btn-row" style={{ flexWrap: 'nowrap' }}>
                        <button
                          className="btn-link"
                          type="button"
                          disabled={busy}
                          onClick={() => startEdit(p)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-link btn-link-danger"
                          type="button"
                          onClick={() => setCondemned(p)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows !== null && rows.length === 0 && (
          <p className="hint" style={{ margin: '16px 0 0' }}>
            No pipelines yet. Every run must name one — create the first above.
          </p>
        )}
      </section>

      <DangerZone token={token} />

      {condemned && (
        <ConfirmDanger
          title="Delete this pipeline?"
          warning={
            <>
              <strong>{condemned.name}</strong> disappears from the run dropdowns. Runs already
              made with it keep their results and their engine stamp — only the shortcut goes.
            </>
          }
          phrase="delete"
          actionLabel="Delete the pipeline"
          busyLabel="Deleting…"
          onRun={() => api.deletePipeline(token, condemned.id).then(() => undefined)}
          onDone={(done) => {
            setCondemned(null);
            if (done) {
              if (editing && editing.id === condemned.id) resetForm();
              void load();
            }
          }}
        />
      )}
    </>
  );
}

/**
 * THE CLEAN SLATE — "I want to be focused and I see a lot of noise from
 * tests." Deletes every project and everything under them; only accounts
 * (and these pipelines) survive.
 */
function DangerZone({ token }: { token: string }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <section className="card">
      <div className="card-head">
        <h2>Danger zone</h2>
      </div>
      <p className="hint">
        Delete all data: every project and everything under it — recordings, versions, answers,
        tasks, calls and stored audio. Accounts and pipelines survive. Permanent.
      </p>
      {note && (
        <div className="notice notice-ok" role="status">
          {note}
        </div>
      )}
      <div className="btn-row">
        <button
          className="btn-danger"
          type="button"
          style={{ width: 'auto' }}
          onClick={() => setConfirming(true)}
        >
          Delete all data…
        </button>
      </div>
      {confirming && (
        <ConfirmDanger
          title="Delete everything, for ever?"
          warning={
            <>
              Every project goes, and everything under them — the audio, every version, every
              answer, every task and every call, for everyone, permanently. Accounts and
              pipelines survive. There is no undo and no trash.
            </>
          }
          phrase="delete"
          actionLabel="Delete everything for ever"
          busyLabel="Deleting…"
          onRun={async () => {
            const res = await api.wipeAllData(token, 'delete');
            setNote(
              res.recordings === 0
                ? 'Everything is already clean.'
                : `Deleted ${res.recordings} recording${res.recordings === 1 ? '' : 's'} and every project.`,
            );
          }}
          onDone={() => setConfirming(false)}
        />
      )}
    </section>
  );
}
